import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import OutlookCalendarCard from "../components/OutlookCalendarCard";

/**
 * Family calendar. With Outlook connected (card at the top), Outlook is the master copy: its events appear here
 * by themselves and anything added or changed here is saved to Outlook. Kids see their own events in the app's
 * "What's on" screen. Backend: /api/events, /api/calendar/*.
 */
export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [kids, setKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | eventObject
  const [monthOffset, setMonthOffset] = useState(0);

  useEffect(() => {
    load();
    const r = new URLSearchParams(window.location.search).get("outlook");
    if (r === "connected") toast.success("Outlook connected. Your events are on their way in.");
    else if (r === "failed") toast.error("Outlook sign-in didn't work. Please try again.");
    if (r) window.history.replaceState(null, "", window.location.pathname);
  }, []);

  async function load() {
    setLoading(true);
    try {
      const [ev, ks] = await Promise.all([api.get("/events"), api.get("/kids")]);
      setEvents(ev.data);
      setKids(ks.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  }

  const today = new Date();
  const view = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const monthName = view.toLocaleString(undefined, { month: "long", year: "numeric" });

  const eventsByDate = useMemo(() => {
    const map = {};
    for (const e of events) {
      if (!e.start_at) continue;
      const d = new Date(e.start_at);
      const key = dayKey(d);
      if (!map[key]) map[key] = [];
      map[key].push(e);
      // Weekly recurrence — also register on every same-weekday cell of the shown month
      if (e.recurrence === "weekly") {
        const startWeekday = d.getDay();
        const first = new Date(view.getFullYear(), view.getMonth(), 1);
        const last = new Date(view.getFullYear(), view.getMonth() + 1, 0);
        for (let day = 1; day <= last.getDate(); day++) {
          const cell = new Date(view.getFullYear(), view.getMonth(), day);
          if (cell.getDay() === startWeekday) {
            const ck = dayKey(cell);
            if (ck === key) continue;
            if (!map[ck]) map[ck] = [];
            map[ck].push({ ...e, _virtual: true });
          }
        }
      }
    }
    return map;
  }, [events, view.getFullYear(), view.getMonth()]);

  const cells = useMemo(() => {
    const first = new Date(view.getFullYear(), view.getMonth(), 1);
    const last = new Date(view.getFullYear(), view.getMonth() + 1, 0);
    const startPad = first.getDay(); // 0=Sun
    const out = [];
    for (let i = 0; i < startPad; i++) out.push(null);
    for (let d = 1; d <= last.getDate(); d++) {
      out.push(new Date(view.getFullYear(), view.getMonth(), d));
    }
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [view.getFullYear(), view.getMonth()]);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[32px] md:text-[38px] font-bold tracking-tight text-[#2C342A]">Calendar</h1>
          <p className="text-sm text-[#707A6E] mt-1">School, sport and everything else the kids have on. They see their own events in Yoda Watch.</p>
        </div>
        <button
          data-testid="add-event-btn"
          onClick={() => setEditing("new")}
          className="rounded-xl bg-[#2C342A] px-4 py-2 text-sm font-medium text-[#F5F4F0] hover:bg-[#2C342A]"
        >
          + Add event
        </button>
      </div>

      <OutlookCalendarCard kids={kids} onChanged={load} />

      <div className="rounded-2xl bg-[#FAFAF7] border border-[#F0EFEB] p-5">
        <div className="flex items-center justify-between mb-4">
          <button data-testid="prev-month" onClick={() => setMonthOffset((n) => n - 1)}
                  className="rounded-lg px-3 py-1.5 hover:bg-[#F0EFEB]">‹</button>
          <div className="font-heading text-xl">{monthName}</div>
          <button data-testid="next-month" onClick={() => setMonthOffset((n) => n + 1)}
                  className="rounded-lg px-3 py-1.5 hover:bg-[#F0EFEB]">›</button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-xs uppercase tracking-wider text-[#707A6E] mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
            <div key={d} className="text-center py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((cell, i) => {
            if (!cell) return <div key={i} className="min-h-[80px]"></div>;
            const key = dayKey(cell);
            const isToday = cell.toDateString() === today.toDateString();
            const dayEvents = eventsByDate[key] || [];
            return (
              <div key={i}
                   className={`min-h-[80px] rounded-lg border p-1.5 ${isToday ? "border-[#2C342A] bg-[#FAFAF7]" : "border-[#F0EFEB] bg-white/60"}`}
                   data-testid={`cal-day-${key}`}>
                <div className={`text-xs mb-1 ${isToday ? "font-bold text-[#2C342A]" : "text-[#707A6E]"}`}>{cell.getDate()}</div>
                <div className="space-y-0.5">
                  {dayEvents.slice(0, 3).map((e, ix) => (
                    <button
                      key={ix}
                      onClick={() => !e._virtual && setEditing(e)}
                      className="w-full text-left text-[10px] rounded px-1.5 py-0.5 truncate"
                      style={{ background: (e.color || "#6B9F80") + "26", color: e.color || "#4B6640",
                               opacity: e.audience === "parents" ? 0.55 : 1 }}
                      title={e.audience === "parents" ? "Parents only: no kid sees this" : e.title}
                      data-testid={`cal-event-${e.id}`}
                    >
                      {e.emoji} {e.title}
                    </button>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-[10px] text-[#707A6E]">+{dayEvents.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {loading && <div className="text-sm text-[#707A6E]">Loading…</div>}

      {editing && (
        <EventEditor
          initial={editing === "new" ? null : editing}
          kids={kids}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EventEditor({ initial, kids, onClose, onSaved }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [emoji, setEmoji] = useState(initial?.emoji || "📅");
  const [color, setColor] = useState(initial?.color || "#6B9F80");
  const [location, setLocation] = useState(initial?.location || "");
  const [startAt, setStartAt] = useState(dtLocal(initial?.start_at ? new Date(initial.start_at) : new Date()));
  const [endAt, setEndAt] = useState(dtLocal(initial?.end_at ? new Date(initial.end_at) : new Date(Date.now() + 60 * 60 * 1000)));
  const [recurrence, setRecurrence] = useState(initial?.recurrence || "none");
  const [kidIds, setKidIds] = useState(initial?.kid_ids || []);
  const [parentsOnly, setParentsOnly] = useState(initial?.audience === "parents");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [remind, setRemind] = useState(initial?.remind_minutes ?? "");
  const [pauseBefore, setPauseBefore] = useState(initial?.pause_before_minutes ?? "");
  const fromOutlook = initial?.source === "outlook";
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const body = {
        title, emoji, color, location, notes,
        start_at: new Date(startAt).toISOString(),
        end_at: new Date(endAt).toISOString(),
        kid_ids: kidIds,
        audience: parentsOnly ? "parents" : (kidIds.length ? "kids" : "everyone"),
      };
      if (!fromOutlook) body.recurrence = recurrence;
      if (remind === "") body.clear_remind = true; else body.remind_minutes = Number(remind);
      if (pauseBefore === "") body.clear_pause = true; else body.pause_before_minutes = Number(pauseBefore);
      if (!initial?.id) { delete body.clear_remind; delete body.clear_pause; }
      if (initial?.id) await api.patch(`/events/${initial.event_id || initial.id}`, body);
      else await api.post("/events", body);
      toast.success(initial?.id ? "Event updated" : "Event created");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setSaving(false); }
  }

  async function remove() {
    if (!initial?.id) return;
    if (!confirm(fromOutlook ? "Delete this event from Outlook too?" : "Delete this event?")) return;
    setSaving(true);
    try {
      await api.delete(`/events/${initial.event_id || initial.id}`);
      toast.success("Event deleted");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-[#FAFAF7] p-6 space-y-4" onClick={(e) => e.stopPropagation()}
           data-testid="event-editor">
        <h3 className="font-heading text-xl">{initial?.id ? "Edit event" : "New event"}</h3>
        {fromOutlook && (
          <p className="text-xs text-[#707A6E] -mt-2">From Outlook. Title, time and place save back to Outlook (this date
            only). Who's going, the reminder and the note apply to every date of this event.</p>
        )}
        <div className="grid grid-cols-[auto,1fr,auto] gap-2 items-center">
          <input value={emoji} onChange={(e) => setEmoji(e.target.value)} className="w-16 rounded-lg border border-[#F0EFEB] px-3 py-2 text-center bg-white" data-testid="event-emoji" />
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. Football training)"
                 className="rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white" data-testid="event-title" />
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-10 w-16 rounded-lg cursor-pointer" data-testid="event-color" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">
            <div className="text-[#707A6E] mb-1">Starts</div>
            <input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)}
                   className="w-full rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white" data-testid="event-start" />
          </label>
          <label className="text-sm">
            <div className="text-[#707A6E] mb-1">Ends</div>
            <input type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)}
                   className="w-full rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white" data-testid="event-end" />
          </label>
        </div>
        <label className="text-sm block">
          <div className="text-[#707A6E] mb-1">Location (optional)</div>
          <input value={location} onChange={(e) => setLocation(e.target.value)} className="w-full rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white" data-testid="event-location" />
        </label>
        {!fromOutlook && <label className="text-sm block">
          <div className="text-[#707A6E] mb-1">Repeats</div>
          <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)} className="w-full rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white" data-testid="event-recurrence">
            <option value="none">Once</option>
            <option value="weekly">Every week (same day)</option>
          </select>
        </label>}
        <div className="text-sm">
          <div className="text-[#707A6E] mb-1">Who it applies to</div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => { setKidIds([]); setParentsOnly(false); }}
                    className={`rounded-full px-3 py-1 text-xs ${kidIds.length === 0 && !parentsOnly ? "bg-[#2C342A] text-[#F5F4F0]" : "bg-[#F0EFEB]"}`}
                    data-testid="event-kid-all">All kids</button>
            {kids.map((k) => (
              <button key={k.id}
                      onClick={() => { setParentsOnly(false); setKidIds((prev) => prev.includes(k.id) ? prev.filter(x => x !== k.id) : [...prev, k.id]); }}
                      className={`rounded-full px-3 py-1 text-xs ${kidIds.includes(k.id) && !parentsOnly ? "bg-[#2C342A] text-[#F5F4F0]" : "bg-[#F0EFEB]"}`}
                      data-testid={`event-kid-${k.id}`}>{k.name}</button>
            ))}
            <button onClick={() => { setParentsOnly(true); setKidIds([]); }}
                    className={`rounded-full px-3 py-1 text-xs ${parentsOnly ? "bg-[#2C342A] text-[#F5F4F0]" : "bg-[#F0EFEB]"}`}
                    data-testid="event-parents-only">Parents only</button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label>
            <div className="text-[#707A6E] mb-1">Remind the kids</div>
            <select value={remind} onChange={(e) => setRemind(e.target.value)} data-testid="event-remind"
                    className="w-full rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white">
              <option value="">No reminder</option>
              {[5, 10, 15, 30, 60, 120].map((m) => <option key={m} value={m}>{m < 60 ? `${m} min` : `${m / 60} hour${m > 60 ? "s" : ""}`} before</option>)}
            </select>
          </label>
          <label>
            <div className="text-[#707A6E] mb-1">Screens off</div>
            <select value={pauseBefore} onChange={(e) => setPauseBefore(e.target.value)} data-testid="event-pause-before"
                    className="w-full rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white">
              <option value="">Don't switch off</option>
              {[5, 10, 15, 30, 60].map((m) => <option key={m} value={m}>{m} min before, until it ends</option>)}
            </select>
          </label>
        </div>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note for the kids (optional), e.g. bring boots"
                  className="w-full rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white text-sm" rows={2} data-testid="event-notes" />
        <div className="flex justify-between pt-2">
          <div>
            {initial?.id && (
              <button onClick={remove} disabled={saving} className="rounded-lg px-4 py-2 text-sm text-[#C06E52] hover:bg-[#F6E3DC]" data-testid="event-delete">
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm hover:bg-[#F0EFEB]" data-testid="event-cancel">Cancel</button>
            <button onClick={save} disabled={saving || !title} className="rounded-lg bg-[#2C342A] px-4 py-2 text-sm text-[#F5F4F0]" data-testid="event-save">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function dayKey(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function dtLocal(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
